import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fleetAPI } from '../lib/api';
import { useCan } from '../hooks/useCan';
import {
  POLL_MS, Chip, ConfirmDialog, useThumbnail, tallyFrame, videoTone, commsTone, presenceTone, tempTone, ago, apiError, toneStyle,
  TALLY_LABEL, TALLY_TONE, SEVERITY_TONE,
} from '../components/fleet/fleetUi';

const SERVICES = [
  { key: 'encoder', label: 'Encoder', effect: 'This camera\'s OMT feed drops for a few seconds while it restarts.' },
  { key: 'audio', label: 'Audio', effect: 'The operator\'s headset goes quiet for a few seconds.' },
  { key: 'comms', label: 'Comms', effect: 'The operator drops off talkback until it reconnects.' },
];

const COMMAND_TONE = { pending: 'info', sent: 'info', acked: 'ok', failed: 'bad', expired: 'warn' };
const FIELD = 'bg-app-well border border-app-line-chrome rounded px-3 py-1.5 text-sm text-app-fg focus:outline-none focus:border-blue-500';
const BUTTON = 'px-3 py-1.5 text-sm rounded border border-app-line-chrome text-gray-300 hover:text-app-fg hover:bg-app-row-hover disabled:opacity-40';

/** One box: full status, alerts, commands and actions (PRD F9, without config in v1). */
export default function FleetBoxPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canManage = useCan('fleet:manage');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind, ... }
  const [service, setService] = useState('encoder');
  const [name, setName] = useState(null); // null = not editing
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = () => fleetAPI.box(id)
      .then((res) => { if (!cancelled) { setData(res.data); setNotFound(false); setNow(Date.now()); } })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError(apiError(err, 'Could not load the box'));
      });
    load();
    const timer = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [id]);

  const box = data?.box;
  const thumb = useThumbnail(id, box?.online ? box.thumb_seq : 0);

  async function run(key, fn, done) {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      const res = await fn();
      setNotice(done(res));
      const fresh = await fleetAPI.box(id).catch(() => null);
      if (fresh) setData(fresh.data);
      return true;
    } catch (err) {
      setError(apiError(err));
      return false;
    } finally {
      setBusy('');
      setConfirm(null);
    }
  }

  const queued = (what) => () => (box.online
    ? `${what} queued. It reaches the box with its next heartbeat.`
    : `${what} queued. The box is offline: it gets it if it is back within 5 minutes.`);

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16 text-center">
        <p className="text-sm text-gray-400">This box is not in the fleet. It may have been removed.</p>
        <Link to="/fleet" className="inline-block mt-4 text-sm text-blue-500 hover:text-blue-400">Back to the fleet</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-sm text-gray-500">{error || 'Loading…'}</div>;

  const s = data.status || {};
  const v = s.video || {};
  const restartInfo = SERVICES.find((x) => x.key === service);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <Link to="/fleet" className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1 mb-4">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Fleet
      </Link>

      <div className="flex flex-col md:flex-row gap-5 mb-5">
        <div className="md:w-80 flex-shrink-0 rounded-lg overflow-hidden border border-app-line-chrome bg-black aspect-video relative" style={tallyFrame(box.tally, box.online)}>
          {thumb && box.online
            ? <img src={thumb} alt="" className="w-full h-full object-cover" />
            : <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">{box.online ? 'No picture yet' : 'Offline'}</div>}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-app-fg flex flex-wrap items-center gap-2">
            <span className="truncate">{box.name}</span>
            <Chip tone={box.online ? 'ok' : 'bad'}>{box.online ? 'Online' : 'Offline'}</Chip>
            {box.online && box.tally && <Chip tone={TALLY_TONE[box.tally]}>{TALLY_LABEL[box.tally]}</Chip>}
            {!box.in_show && <Chip tone="off">Not in show</Chip>}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {box.source_name && box.source_name !== box.name ? `${box.source_name} · ` : ''}
            {box.hostname || box.machine_id}
            {box.version ? <> · <span className="font-mono text-[13px]">v{box.version}</span></> : null}
            {' · '}last seen {ago(box.last_seen_at, now)}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button className={BUTTON} disabled={!!busy} onClick={() => run('identify', () => fleetAPI.identify(id), queued('Identify'))}>
              {busy === 'identify' ? 'Sending…' : 'Identify'}
            </button>
            <button className={BUTTON} disabled={!!busy} onClick={() => run('tone', () => fleetAPI.testTone(id), queued('Test tone'))}>
              {busy === 'tone' ? 'Sending…' : 'Test tone'}
            </button>
            <span className="inline-flex items-center gap-1">
              <select value={service} onChange={(e) => setService(e.target.value)} className={FIELD} aria-label="Service to restart">
                {SERVICES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
              <button className={BUTTON} disabled={!!busy} onClick={() => setConfirm({ kind: 'restart' })}>Restart</button>
            </span>
            {canManage && (
              <button className={BUTTON} disabled={!!busy} onClick={() => setConfirm({ kind: 'reboot', force: false })} style={{ color: 'var(--live)' }}>Reboot</button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">Identify blinks the tally light blue for 10 s. Test tone plays 1 kHz in the operator&apos;s headset for 3 s.</p>
        </div>
      </div>

      {notice && <div className="mb-4 px-4 py-2.5 rounded text-sm" style={toneStyle('info', 10)}>{notice}</div>}
      {error && <div className="mb-4 px-4 py-2.5 rounded text-sm" style={toneStyle('bad', 10)}>{error}</div>}

      {data.alerts.length > 0 && (
        <Section title="Open alerts">
          <ul className="space-y-1.5">
            {data.alerts.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Chip tone={SEVERITY_TONE[a.severity]}>{a.severity}</Chip>
                <span className="font-mono text-[13px] text-gray-400">{a.code}</span>
                <span className="text-app-fg">{a.message}</span>
                <span className="text-xs text-gray-500 ml-auto">since {ago(a.opened_at, now)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Video">
          <Facts rows={[
            ['State', <Chip key="s" tone={videoTone(v.state)}>{v.state || '–'}</Chip>],
            ['Format', v.format?.mode],
            ['Frame rate', num(v.fps, 2, ' fps')],
            ['Captured / sent', v.captured != null ? `${v.captured} / ${v.sent}` : null, true],
            ['Dropped', v.dropped, true],
            ['Latency p99', v.capture_to_send_p99_ms != null ? `${num(v.capture_to_send_p99_ms, 1)} ms capture→send` : null, true],
            ['OMT', v.omt_address],
            ['OMT connections', v.omt_connections, true],
            ['Bitrate', num(v.video_mbps, 1, ' Mb/s')],
            ['Device', v.device ? `${v.device.model}${v.device.firmware ? ` (fw ${v.device.firmware})` : ''}` : (v.fake_capture ? 'Fake capture' : null)],
          ]} />
        </Section>

        <Section title="Tally">
          <Facts rows={[
            ['State', s.tally ? <Chip key="t" tone={TALLY_TONE[s.tally.state]}>{TALLY_LABEL[s.tally.state] || s.tally.state}</Chip> : null],
            ['Mode', s.tally?.mode],
            ['From OMT', s.tally?.omt],
            ['From vMix API', s.tally?.vmix_api],
            ['Lights', s.tally?.lights?.length ? s.tally.lights.map((l) => `${l.serial || 'light'} ${l.present ? 'present' : 'missing'}`).join(', ') : 'none'],
          ]} />
        </Section>

        <Section title="Comms">
          <Facts rows={[
            ['State', s.comms ? <Chip key="c" tone={commsTone(s.comms.state)}>{s.comms.state}</Chip> : null],
            ['Channel', s.comms?.channel_name],
            ['Error', s.comms?.error],
          ]} />
          {s.comms?.members?.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {s.comms.members.map((m) => (
                <li key={m.id} className="px-2 py-0.5 rounded text-xs border border-app-line-chrome" style={m.speaking ? toneStyle('ok', 16) : undefined}>
                  {m.name}{m.self ? ' (this box)' : ''}{m.ignored ? ' · muted' : ''}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Audio">
          <Facts rows={[
            ['Headset', s.audio ? <Chip key="h" tone={presenceTone(s.audio.headset)}>{s.audio.headset}</Chip> : null],
            ['Programme', s.audio ? <Chip key="p" tone={presenceTone(s.audio.programme)}>{s.audio.programme}</Chip> : null],
            ['Mix', s.audio?.mix_mode],
            ['PTT', s.audio ? `${s.audio.ptt_device ? 'device present' : 'no device'}${s.audio.ptt_active ? ', talking' : ''}` : null],
          ]} />
          {s.audio?.meters && (
            <div className="mt-3 space-y-1.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Meters (snapshot)</div>
              {Object.entries(s.audio.meters).map(([k, m]) => <Meter key={k} label={k.replace('_', ' ')} meter={m} />)}
            </div>
          )}
        </Section>

        <Section title="System">
          <Facts rows={[
            ['CPU', s.system ? `${num(s.system.cpu_percent, 0)}%` : null, true],
            ['Temperature', s.system?.cpu_temp_c != null ? <span key="temp" className="font-mono text-[13px]" style={{ color: tempTone(s.system.cpu_temp_c) === 'ok' ? undefined : `var(${tempTone(s.system.cpu_temp_c) === 'bad' ? '--live' : '--next'})` }}>{Math.round(s.system.cpu_temp_c)}°C</span> : null],
            ['Memory', s.system ? `${Math.round(s.system.mem_used_mb)} MB` : null, true],
            ['Load', s.system?.load?.map((x) => x.toFixed(2)).join(' '), true],
            ['Uptime', s.system ? duration(s.system.uptime_s) : null, true],
            ['Link', s.system?.link_speed_mbps != null ? `${s.system.link_speed_mbps} Mb/s` : null, true],
            ['Data free', s.system?.data_free_mb != null ? `${Math.round(s.system.data_free_mb / 1024)} GB` : null, true],
            ['Update', s.update ? `${s.update.state}${s.update.state !== 'idle' ? ` ${Math.round((s.update.progress || 0) * 100)}%` : ''}${s.update.message ? ` · ${s.update.message}` : ''}` : null],
          ]} />
        </Section>

        <Section title="Identity">
          <Facts rows={[
            ['Hostname', s.identity?.hostname],
            ['Machine id', s.identity?.machine_id, true],
            ['Addresses', s.identity?.ip?.join(', '), true],
            ['MAC', s.identity?.mac, true],
            ['Software', s.software ? `${s.software.version} (slot ${s.software.slot})` : null, true],
            ['Config hash', box.config_hash ? box.config_hash.slice(0, 12) : null, true],
            ['Enrolled', box.adopted_at ? new Date(box.adopted_at).toLocaleString() : null],
          ]} />
        </Section>
      </div>

      <Section title="Recent commands">
        {data.commands.length === 0
          ? <p className="text-xs text-gray-500">None yet.</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-app-line-chrome">
                  {data.commands.map((c) => (
                    <tr key={c.id}>
                      <td className="py-1.5 pr-3 text-app-fg whitespace-nowrap">{c.kind}{c.args?.service ? ` ${c.args.service}` : ''}{c.args?.force ? ' (force)' : ''}</td>
                      <td className="py-1.5 pr-3"><Chip tone={COMMAND_TONE[c.state]}>{c.state}</Chip></td>
                      <td className="py-1.5 pr-3 text-xs text-gray-500 whitespace-nowrap">{ago(c.created_at, now)}</td>
                      <td className="py-1.5 text-xs text-gray-400">{c.error || (c.attempts > 1 ? `sent ${c.attempts} times` : '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Section>

      {canManage && (
        <Section title="Manage">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-400 w-24">Name</span>
              {name === null ? (
                <>
                  <span className="text-sm text-app-fg">{box.name}</span>
                  <button className="text-xs text-blue-500 hover:text-blue-400" onClick={() => setName(box.name)}>Rename</button>
                </>
              ) : (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const ok = await run('rename', () => fleetAPI.update(id, { name: name.trim() || null }), () => 'Renamed.');
                    if (ok) setName(null);
                  }}
                >
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className={FIELD} autoFocus placeholder={box.source_name || ''} />
                  <button type="submit" className={BUTTON} disabled={!!busy}>Save</button>
                  <button type="button" className="text-xs text-gray-500 hover:text-app-fg" onClick={() => setName(null)}>Cancel</button>
                </form>
              )}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <span className="text-sm text-gray-400 w-24 pt-0.5">In show</span>
              <input
                type="checkbox"
                className="mt-1"
                checked={box.in_show}
                disabled={!!busy}
                onChange={(e) => run('inshow', () => fleetAPI.update(id, { in_show: e.target.checked }), () => (e.target.checked ? 'In the show: alerts are on.' : 'Out of the show: this box raises no alerts.'))}
              />
              <span className="text-xs text-gray-500">Spares and bench units should be out of the show: they never raise alerts.</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400 w-24">Remove</span>
              <button className={BUTTON} style={{ color: 'var(--live)' }} disabled={!!busy} onClick={() => setConfirm({ kind: 'remove' })}>Remove from fleet</button>
            </div>
          </div>
        </Section>
      )}

      {confirm?.kind === 'restart' && (
        <ConfirmDialog
          title={`Restart ${restartInfo.label.toLowerCase()} on ${box.name}?`}
          confirmLabel={`Restart ${restartInfo.label.toLowerCase()}`}
          danger
          busy={busy === 'restart'}
          onCancel={() => setConfirm(null)}
          onConfirm={() => run('restart', () => fleetAPI.restart(id, service), queued(`Restart ${service}`))}
        >
          <p>{restartInfo.effect}</p>
          {box.tally === 'program' && <p style={{ color: 'var(--live)' }}>This camera is on program now.</p>}
        </ConfirmDialog>
      )}
      {confirm?.kind === 'reboot' && (
        <ConfirmDialog
          title={`Reboot ${box.name}?`}
          confirmLabel="Reboot"
          danger
          busy={busy === 'reboot'}
          onCancel={() => setConfirm(null)}
          onConfirm={() => run('reboot', () => fleetAPI.reboot(id, confirm.force), queued('Reboot'))}
        >
          <p>The camera feed, tally and talkback are gone until the box is back, usually under a minute.</p>
          {box.tally === 'program' && <p style={{ color: 'var(--live)' }}>This camera is on program now.</p>}
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input type="checkbox" checked={confirm.force} onChange={(e) => setConfirm({ ...confirm, force: e.target.checked })} />
            Reboot even if an update is in progress
          </label>
        </ConfirmDialog>
      )}
      {confirm?.kind === 'remove' && (
        <ConfirmDialog
          title={`Remove ${box.name} from the fleet?`}
          confirmLabel="Remove"
          danger
          busy={busy === 'remove'}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            const ok = await run('remove', () => fleetAPI.remove(id), () => 'Removed.');
            if (ok) navigate('/fleet');
          }}
        >
          <p>Its fleet token is revoked at once: the box stops appearing here and gets no more commands. It keeps working on its own.</p>
          <p className="text-gray-400">To bring it back, enrol it again with a code; its name comes back with it.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-4 rounded-lg border border-app-line-chrome bg-app-well px-4 py-3">
      <h2 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">{title}</h2>
      {children}
    </section>
  );
}

/** Label/value rows; a third element `true` sets the value in mono (numbers, ids). */
function Facts({ rows }) {
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-3 gap-y-1 text-sm">
      {rows.filter(([, value]) => value != null && value !== '').map(([label, value, mono]) => (
        <React.Fragment key={label}>
          <dt className="text-gray-500">{label}</dt>
          <dd className={`min-w-0 break-words ${mono ? 'font-mono text-[13px] text-gray-300' : 'text-app-fg'}`}>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Meter({ label, meter }) {
  const pct = (db) => Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
  const tone = meter.peak_dbfs > -3 ? '--live' : meter.peak_dbfs > -12 ? '--next' : '--green';
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-xs text-gray-500 capitalize">{label}</span>
      <div className="flex-1 h-1.5 rounded bg-app-canvas overflow-hidden relative">
        <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${pct(meter.rms_dbfs)}%`, background: `var(${tone})`, opacity: 0.55 }} />
        <div className="absolute inset-y-0 w-0.5" style={{ left: `${pct(meter.peak_dbfs)}%`, background: `var(${tone})` }} />
      </div>
      <span className="w-16 text-right font-mono text-[13px] text-gray-400">{meter.peak_dbfs > -120 ? meter.peak_dbfs.toFixed(0) : '-∞'} dB</span>
    </div>
  );
}

function num(n, digits, suffix = '') {
  if (n == null || Number.isNaN(Number(n))) return null;
  return `${Number(n).toFixed(digits)}${suffix}`;
}

function duration(seconds) {
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}
